import { Outlet, useLocation } from 'react-router';
import { useGlobalTableDragPan } from '@/shared/hooks/useTableDragPan';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HouseSidebar } from './HouseSidebar';
import { Topbar, pageLabel } from './Topbar';
import { usePresence } from './presence/usePresence';
import { NotificationBell } from './notifications/NotificationBell';
import { Footer } from './Footer';
import { SearchOverlay } from './SearchOverlay';
import { FreyaWidget } from '@/features/coportal/chatbot/FreyaWidget';
import { resolveModuleFromPath } from './moduleNav';
import { isRouteOff, useModuleTree } from '@/shared/modules/store';
import { ModuleInkStyle } from '@/shared/modules/ModuleInkStyle';
import { NotFoundPage } from '@/features/NotFoundPage';
import { SHELL_MOBILE_QUERY, useMediaMatch } from './useMediaMatch';
import { pageKey } from './pageKey';
import { OnboardingHost } from '@/features/onboarding/OnboardingHost';
import { TutorialsHost } from '@/features/tutorials/TutorialsHost';

// BUG-QAr9r7-14/15 — LiveAnnouncerProvider is mounted in App.tsx (above
// the route switch) so anonymous routes also see the provider. Do NOT
// re-introduce a nested provider here.

type Theme = 'light' | 'dark';

const THEME_KEY = 'hub.theme';

/**
 * Initial theme. The interface is paper first: the pre-boot script in
 * index.html stores 'light' the first time a browser meets it, and after that
 * the sidebar's theme toggle is the person's own.
 */
function initialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_KEY) as Theme | null;
  if (stored === 'light' || stored === 'dark') return stored;
  return 'light';
}

/**
 * The Amassa shell.
 *
 *   ┌──────────┬────────────────────────────┐
 *   │ Sidebar  │ Topbar                      │
 *   │ (mark,   │ ──────────────────────────  │
 *   │ switcher,│ main workspace              │
 *   │ pages)   │                             │
 *   └──────────┴────────────────────────────┘
 *
 * One sidebar in Quotinc's style (HouseSidebar): the mark, a module
 * switcher, the open module's pages, the person at the foot. On the Hub
 * home it lists the modules themselves. (The Dock + ModuleRail pair of the
 * retired Woven Signal interface was removed 2026-10-01.)
 */
export function Shell() {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [searchOpen, setSearchOpen] = useState(false);
  // ── MOBILE ── off-canvas nav drawer state (no-op on desktop where the
  // rails render inline — see `.shell-nav` / `.topbar__hamburger` in App.css).
  const [navOpen, setNavOpen] = useState(false);
  const isMobile = useMediaMatch(SHELL_MOBILE_QUERY);
  const navRef = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();
  // Presence heartbeat lives HERE, not in the Topbar: the Shell is mounted
  // once per tab and survives navigation, so there is exactly one timer.
  const presence = usePresence(pathname);

  const activeModule = resolveModuleFromPath(pathname);
  const routeOff = isRouteOff(pathname, useModuleTree());
  const onHome = pathname === '/dashboard' || pathname === '/';

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((t) => (t === 'light' ? 'dark' : 'light')),
    [],
  );
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const toggleNav = useCallback(() => setNavOpen((v) => !v), []);
  const closeNav = useCallback(() => setNavOpen(false), []);

  // ── MOBILE ── close the drawer on route change (a nav tap navigates).
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  // Grab-and-pan on EVERY `.hub-table-wrap` in the app, from one
  // delegated listener (owner directive 2026-08-11). Tables that pan
  // themselves — the logs table, via useLogsTableInteractions — opt out
  // by carrying `data-logs-scroller`.
  useGlobalTableDragPan();

  // ── Document title, for every page, from one place ────────────────
  //
  // The 2026-08-10 directive strips the `<h1>` from every module sub-page,
  // which leaves the browser tab as one of only two carriers of the page
  // name (the breadcrumb is the other). Setting it here rather than in each
  // page means ~41 pages cannot drift from the crumb — both read the same
  // `pageLabel()`. Pages that genuinely need a dynamic title (a record name,
  // an unsaved marker) can still override it in their own effect; theirs
  // runs after this one on the same navigation.
  useEffect(() => {
    const { module, page } = pageLabel(pathname);
    document.title = [page, module, 'Amassa'].filter(Boolean).join(' · ');
  }, [pathname]);

  // ── MOBILE ── the drawer only exists at mobile widths; if we cross back
  // to desktop (rotate / resize) drop the open state so nothing lingers.
  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);

  // ── MOBILE ── Esc closes the drawer; lock body scroll + move focus into
  // the panel while it is open so it behaves like a modal dialog.
  useEffect(() => {
    if (!(navOpen && isMobile)) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    navRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [navOpen, isMobile]);

  // Cmd/Ctrl+K — toggle the command bar. Defer to editable fields unless
  // the overlay is already open (then the shortcut always closes it).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K'))) return;
      if (!searchOpen) {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        const isEditable =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          t?.isContentEditable === true;
        if (isEditable) return;
      }
      e.preventDefault();
      setSearchOpen((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  return (
    <div
      className="amassa-shell"
      // The open module's ink joins the light field behind the plates
      // (skin-quotinc.css §13).
      data-mod={!onHome ? (activeModule ?? undefined) : undefined}
      style={{
        position: 'relative',
        display: 'flex',
        minHeight: '100dvh',
        background: 'var(--bg)',
        color: 'var(--text)',
      }}
    >
      {/* The tenant's module inks (Admin ▸ Modules); nothing when unchanged. */}
      <ModuleInkStyle />
      {/* ── MOBILE ── the sidebar lives in an off-canvas panel. On desktop
          `.shell-nav` is `display:contents`, so the sidebar stays a direct
          flex child of the shell.
          At ≤768px the panel becomes a fixed drawer that slides in over a
          scrim, toggled by the Topbar hamburger. */}
      <div
        id="shell-nav"
        ref={navRef}
        tabIndex={-1}
        className={`shell-nav${navOpen ? ' is-open' : ''}`}
        role={isMobile && navOpen ? 'dialog' : undefined}
        aria-modal={isMobile && navOpen ? true : undefined}
        aria-label={isMobile && navOpen ? 'Navigation' : undefined}
        inert={isMobile && !navOpen ? true : undefined}
      >
        <HouseSidebar
          module={onHome ? null : activeModule}
          pathname={pathname}
          theme={theme}
          onToggleTheme={toggleTheme}
          onOpenSearch={openSearch}
        />
      </div>

      {/* ── MOBILE ── backdrop; only painted while the drawer is open. */}
      {isMobile && navOpen && (
        <div className="shell-scrim" onClick={closeNav} aria-hidden />
      )}

      <div
        className="amassa-workspace"
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Topbar
          pathname={pathname}
          presence={presence}
          bell={<NotificationBell />}
          onOpenSearch={openSearch}
          onToggleNav={toggleNav}
          navOpen={navOpen}
        />
        <main
          id="main"
          style={{
            flex: 1,
            padding: 'var(--hm-shell-pad)',
            // `clip`, NOT `hidden`. Per CSS Overflow 3 §3.1, `overflow-x:
            // hidden` with a `visible` y computes y to `auto`, which turns
            // <main> into a scroll CONTAINER — and since the shell is
            // `min-height: 100dvh` (not `height`), <main> grows with its
            // content and never actually scrolls. Every `position: sticky`
            // descendant then picks <main> as its scrollport, finds a
            // scrollport that never moves, and stays put in flow: the logs
            // toolbar's `top: var(--topbar-h)` was silently inert.
            // `clip` suppresses the same horizontal overflow WITHOUT
            // creating a scroll container, so the document stays the
            // scrollport and sticky works.
            overflowX: 'clip',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div key={pageKey(pathname)} className="motion-page">
            {/* A page or module the tenant switched off (Admin ▸ Modules)
                renders not-found, never /forbidden (brief §4.3). */}
            {routeOff ? <NotFoundPage /> : <Outlet />}
          </div>
        </main>
        <Footer />
      </div>

      <SearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        activeModule={activeModule}
      />

      {/* Onboarding (2026-10-27): the invited colleague's welcome and tour,
          the replayable tour, a new workspace's first trip to the wizard.
          Nothing for Asian Trails' existing accounts. */}
      <OnboardingHost theme={theme} onSetTheme={setTheme} />

      {/* Tutorials (2026-10-27): the running lesson over practice records,
          started from Preferences ▸ Tutorials. Never starts by itself. */}
      <TutorialsHost />

      {/* Freya. Scopes itself to /coportal and renders nothing when the
          assistant is switched off or unconfigured — see FreyaWidget. */}
      <FreyaWidget />
    </div>
  );
}
